import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Chip
} from '@mui/material';
import { CheckGreenCircle, CloseRoundIcon, ConfirmationIcon } from 'assets';
import { DeleteEmojiIcon, UploadFileIcon } from 'assets/icon';
import { ApiPaths } from 'models/apiPaths';
import AdminButton from 'pages/components/button';
import DialogActionWall from 'pages/components/dialogActionWall';
import TypographyWithI18N from 'pages/components/TypographyWithI18N';
import { useState } from 'react';
import { axiosGlobalReport } from 'services/api';
import { colors } from 'styles/colors';

const ReportProblem = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const [reportText, setReportText] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
    const [fileErrorMessage, setFileErrorMessage] = useState('');
    const [confirmDialog, setConfirmDialog] = useState<boolean>(false);

    const onFileChange = (event: any) => {
        var files: any[] = [];
        Object.entries(event.target.files).forEach(
            ([eventName, eventHandler]: any) => {
                if (eventHandler.type.indexOf('image/') > -1) {
                    files.push(eventHandler);
                } else if (eventHandler.type.indexOf('video/') > -1) {
                    files.push(eventHandler);
                }
            }
        );
        const total = uploadedFiles.length + files.length;
        files.length > 0 && setUploadedFiles([...uploadedFiles, ...files]);
    };

    const handleClose = () => {
        setReportText('');
        setUploadedFiles([]);
        setFileErrorMessage('');
        onClose();
    };

    const reportProblemApi = async () => {
        try {
            setLoader(true);
            const formData = new FormData();
            uploadedFiles.forEach((file) => {
                formData.append('file', file);
            });

            formData.append('description', reportText);

            const globalReport = await axiosGlobalReport(ApiPaths.USER_REPORT_ISSUE, formData);

            if (globalReport) {
                setConfirmDialog(false); 
                setShowSuccessModal(true);
                handleClose();
            }
        } catch (error: any) {
            const errTxt = error.response?.data?.message
                ? error.response.data.message
                : error.response?.data?.error;
            console.log(errTxt);
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth  maxWidth="md" >
                <DialogTitle>Report a Problem</DialogTitle>
                <DialogContent
                 dividers={false} 
                    sx={{
                        maxHeight: 'unset',
                        overflow: 'visible'
                    }}>
                
		    <TextField
                        autoFocus
                        margin="dense"
                        placeholder="Add your Problem description..."
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={reportText}
                        onChange={(e) => setReportText(e.target.value)}
                        inputProps={{
                            sx: { paddingLeft: "24px" },
                            maxLength: 1000
                        }}
                        FormHelperTextProps={{
                            sx: { color: "rgb(255, 0, 0)" }
                        }}
                        helperText={
                            reportText.length >= 1000
                                ? "Character limit cannot exceed 1000"
                                : ''
                        }
                    />
                </DialogContent>

                <TypographyWithI18N
                    id={'uploadMedia'}
                    className="bodyText"
                    sx={{
                        color: colors.text.darkGray,
                        my: 1,
                        marginLeft: '24px'
                    }}
                    i18nKey={'Upload Media'}
                    tabIndex={0}
                />

                <Box position={'relative'} width={'94%'} height={200} marginLeft={'24px'} marginRight={'16px'} flexShrink={0}>
                    <input
                        multiple
                        id="fileUploadButton"
                        accept="image/*,video/*"
                        type="file"
                        onChange={onFileChange}
                        style={{
                            height: '100%',
                            width: '100%',
                            position: 'absolute',
                            opacity: 0,
                            cursor: 'pointer'
                        }}
                    />
                    <Box
                        id="medialFile"
                        className="flexCenterColumn flexCenterJustify"
                        sx={{
                            background: colors.background.approxWhiteSmoke,
                            height: '100%',
                            width: '100%',
                            cursor: 'pointer'
                        }}
                    >
                        <UploadFileIcon />
                        <TypographyWithI18N
                            className="header"
                            i18nKey={'Add Photos or Video'}
                            color={colors.text.primaryBlack}
                        />
                        <TypographyWithI18N className="metadata" i18nKey={'or drag and drop'} />
                        <TypographyWithI18N
                            className="metadata"
                            i18nKey={'Supported formats - .jpeg, .jpg, .png, .mov, .mp4, etc.'}
                        />
                    </Box>
                </Box>

          
                <Box
                    py={2}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        flexShrink: 0
                    }}
                >
                    {uploadedFiles.map((file, index) => (
                        <Chip
                            key={index.toString()}
                            label={file.name}
                            color="primary"
                            variant="outlined"
                            onDelete={() => {
                                const updatedFiles = uploadedFiles.filter((x) => x.lastModified !== file.lastModified);
                                setUploadedFiles(updatedFiles);
                            }}
                            deleteIcon={<CloseRoundIcon />}
                            sx={{
                                background: '#D4FFFF',
                                borderRadius: '6px'
                            }}
                        />
                    ))}
                </Box>

                {fileErrorMessage && (
                    <Box sx={{ color: 'red', fontSize: 12, mb: 1, ml: 2 }}>{fileErrorMessage}</Box>
                )}

           
                <DialogActions sx={{ marginRight: '16px', marginBottom: '16px' }}>
                    <AdminButton
                        title={'cancel'}
                        onClick={handleClose}
                        variant='outlined'
                        role='presentation'
                        aria-label={`Button Press Enter to Exit the Window`}
                        sxProps={{
                            minWidth: { xs: '60px', sm: '164px' },
                            height: { xs: '30px', sm: 44 },
                            fontSize: { xs: '12px !important', sm: '16px !important' },
                            '&.MuiButton-outlined': {
                                border: `1px solid ${colors.text.approxVeryLightGrey}`,
                                background: colors.text.primaryGray,
                                color: colors.text.clearButton
                            },
                        }}
                    />
                    <AdminButton
                        title={'Send'}
                        role='presentation'
                        aria-label={`Button Press Enter to Submit`}
                        color={'primary'}
                        onClick={() => {
                            if (reportText.trim().length > 0 && !loader) {
                                setConfirmDialog(true);
                            }
                        }}
                        disabled={loader}
                        sxProps={{
                            minWidth: { xs: '60px', sm: '164px' },
                            height: { xs: '30px', sm: 44 },
                            fontSize: { xs: '12px !important', sm: '16px !important' },
                            pointerEvents: reportText.trim().length === 0 || loader ? 'none' : 'auto', 
                            cursor: reportText.trim().length === 0 || loader ? 'default' : 'pointer',
                        }}
                    />
                </DialogActions>
            </Dialog>

            {confirmDialog && (
                <DialogActionWall
                    open={confirmDialog}
                    headerContent={
                        <Box display="flex" justifyContent="center" alignItems="center" marginBottom={'24px'}>
                            <ConfirmationIcon style={{ width: '48px', height: '48px', marginRight: '8px' }} />
                        </Box>
                    }
                    title={'Are you sure you want to Report?'}
                    dialogTitleSxProps={{ textAlign: 'center' }}
                    dialogActionsSxProps={{ justifyContent: 'center' }}
                    onCancel={() => setConfirmDialog(false)}
                    onSuccess={reportProblemApi}
                    successLabel={'Report'}
                    onSuccessBtnColor="error"
                    loaderBtn={loader}
                />
            )}

            {showSuccessModal && (
                <DialogActionWall
                    open={showSuccessModal}
                    title={''}
                    dialogTitleSxProps={{ textAlign: 'center' }}
                    dialogActionsSxProps={{ justifyContent: 'center' }}
                    closeIconButton={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                >
                    <Box className="flexCenterJustify flexCenterColumn">
                        <CheckGreenCircle />
                        <TypographyWithI18N
                            mt={3}
                            className="headerBold"
                            color={colors.text.primaryBlack}
                            i18nKey={'You have successfully Reported'}
                        />
                    </Box>
                </DialogActionWall>
            )}
        </>
    );
};

export default ReportProblem;
